"use client";

import { useState } from "react";
import Image from "next/image";
import Flag from "react-world-flags";
import { LuChevronLeft, LuChevronRight } from "react-icons/lu";
import { FaEarthAfrica, FaEarthAmericas, FaEarthAsia, FaEarthEurope } from "react-icons/fa6";
import { PingChip, SignalBars, latencyColor, latencyText } from "./ping-ui";
import type { Reading, Region } from "./types";

/* Same art the home page uses; the marker percentages are tuned to this image. */
const MAP_SRC = "https://cdn.aurex.lk/world-map-dots.webp";
const MAP_RATIO = "47.91%";

/** A globe turned to the region being listed. */
function RegionEarth({ region }: { region: string }) {
    const props = { size: 17, className: "text-purple-300" };
    const r = region.toLowerCase();
    if (r.includes("america")) return <FaEarthAmericas {...props} />;
    if (r.includes("europe")) return <FaEarthEurope {...props} />;
    if (r.includes("africa")) return <FaEarthAfrica {...props} />;
    return <FaEarthAsia {...props} />;
}

/**
 * Dotted world map with a paged region panel beside it — one page per region,
 * stepped through with the arrows. Every reading is measured in the visitor's
 * own browser, so a location whose probe never answers shows "n/a" rather than
 * an invented figure.
 */
export default function WorldMap({
    regions,
    pings,
    best,
    onRetest,
    onFocus,
}: {
    regions: Region[];
    pings: Record<string, Reading>;
    best: string | null;
    onRetest: (code: string) => void;
    onFocus: (code: string) => void;
}) {
    const [page, setPage] = useState(0);
    const region = regions[Math.min(page, regions.length - 1)];
    const count = region.locations.length;

    const step = (delta: number) => setPage((p) => (p + delta + regions.length) % regions.length);

    return (
        <div className="mt-12 flex flex-col lg:flex-row items-center gap-8 lg:gap-10">
            <div className="w-full lg:w-[58%]">
                <div className="relative w-full select-none" style={{ paddingBottom: MAP_RATIO }}>
                    <Image
                        src={MAP_SRC}
                        alt=""
                        aria-hidden="true"
                        draggable={false}
                        fill
                        sizes="(max-width: 1024px) 100vw, 58vw"
                        className="pointer-events-none"
                        style={{
                            objectFit: "fill",
                            opacity: 0.14,
                            filter: "brightness(0) invert(1) sepia(1) saturate(3) hue-rotate(240deg)",
                        }}
                    />
                    {regions
                        .flatMap((r) => r.locations)
                        .filter((l) => l.map)
                        .map((l) => {
                            const reading = pings[l.code] ?? null;
                            return (
                                <div
                                    key={l.code}
                                    className="group absolute -translate-x-1/2 -translate-y-1/2"
                                    style={{ left: l.map!.left, top: l.map!.top }}
                                >
                                    {/* Hover card: your latency, then the location it belongs to. */}
                                    <div
                                        className="pointer-events-none absolute left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-150 z-20"
                                        style={{ bottom: "calc(100% + 9px)" }}
                                    >
                                        <div
                                            className="rounded-xl px-3 py-2 flex flex-col gap-1.5 whitespace-nowrap"
                                            style={{
                                                background: "rgba(13,13,20,0.94)",
                                                border: "1px solid rgba(255,255,255,0.09)",
                                                boxShadow: "0 12px 32px rgba(0,0,0,0.55)",
                                                backdropFilter: "blur(6px)",
                                            }}
                                        >
                                            <span
                                                className="flex items-center gap-1.5 font-poppins text-xs font-semibold tabular-nums"
                                                style={{ color: latencyColor(reading) }}
                                            >
                                                {!l.soon && <SignalBars reading={reading} />}
                                                {latencyText(reading, l.soon)}
                                            </span>
                                            <span className="flex items-center gap-1.5">
                                                <Flag
                                                    code={l.code}
                                                    alt=""
                                                    style={{ width: 18, height: 12, borderRadius: 2, objectFit: "cover", imageRendering: "pixelated" }}
                                                />
                                                <span className="text-gray-200 font-poppins text-xs">{l.city}</span>
                                            </span>
                                        </div>
                                    </div>

                                    <span className="relative flex w-2.5 h-2.5">
                                        {!l.soon && (
                                            <span
                                                className="absolute inset-0 rounded-full animate-ping"
                                                style={{ background: "rgba(168,85,247,0.5)" }}
                                            />
                                        )}
                                        <span
                                            className="relative w-2.5 h-2.5 rounded-full transition-transform duration-150 group-hover:scale-125"
                                            style={{
                                                background: l.soon ? "rgba(255,255,255,0.25)" : "#a855f7",
                                                boxShadow: l.soon ? "none" : "0 0 12px 3px rgba(168,85,247,0.5)",
                                            }}
                                        />
                                    </span>
                                </div>
                            );
                        })}
                </div>
            </div>

            <div className="w-full lg:w-[42%]">
                <div className="rounded-2xl p-4 sm:p-5" style={{ background: "#19131f79", border: "1px solid #7c44e511" }}>
                    <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2.5">
                            <span
                                className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
                                style={{ background: "rgba(124,58,237,0.18)" }}
                            >
                                <RegionEarth region={region.name} />
                            </span>
                            <div>
                                <p className="text-white font-poppins text-sm font-semibold leading-tight">{region.name}</p>
                                <p className="text-gray-500 font-poppins text-xs mt-0.5">
                                    {count} {count === 1 ? "location" : "locations"}
                                </p>
                            </div>
                        </div>
                        {regions.length > 1 && (
                            <div className="flex items-center gap-1.5">
                                <button
                                    onClick={() => step(-1)}
                                    aria-label="Previous region"
                                    className="w-7 h-7 rounded-lg flex items-center justify-center bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
                                >
                                    <LuChevronLeft size={14} />
                                </button>
                                <button
                                    onClick={() => step(1)}
                                    aria-label="Next region"
                                    className="w-7 h-7 rounded-lg flex items-center justify-center bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
                                >
                                    <LuChevronRight size={14} />
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="mt-4 flex flex-col gap-2">
                        {region.locations.map((l) => {
                            const isBest = l.code === best;
                            return (
                                <div
                                    key={l.code}
                                    className="flex items-center gap-2 rounded-xl pr-2 transition-colors"
                                    style={{
                                        background: isBest ? "rgba(124,58,237,0.16)" : "rgba(255,255,255,0.03)",
                                        border: isBest ? "1px solid rgba(168,85,247,0.5)" : "1px solid rgba(255,255,255,0.05)",
                                    }}
                                >
                                    <button
                                        onClick={() => onFocus(l.code)}
                                        className="group flex items-center gap-2.5 flex-1 min-w-0 text-left px-3 py-2.5 rounded-l-xl"
                                    >
                                        <Flag
                                            code={l.code}
                                            alt={`${l.country} flag`}
                                            style={{ width: 22, height: 15, borderRadius: 2, objectFit: "cover", imageRendering: "pixelated" }}
                                        />
                                        <span className="min-w-0">
                                            {isBest && (
                                                <span className="block font-poppins text-[9px] font-semibold uppercase tracking-wider text-purple-300 leading-none">
                                                    Recommended
                                                </span>
                                            )}
                                            <span className="block text-gray-200 font-poppins text-xs font-medium truncate group-hover:text-white transition-colors">
                                                {l.country}
                                            </span>
                                        </span>
                                    </button>
                                    <PingChip reading={pings[l.code] ?? null} soon={l.soon} onRetest={() => onRetest(l.code)} />
                                </div>
                            );
                        })}
                    </div>

                    <p className="text-gray-600 font-poppins text-[10px] leading-relaxed mt-4">
                        Measured live from your connection to each region. Pick a row to jump to that
                        location&apos;s hardware.
                    </p>
                </div>
            </div>
        </div>
    );
}
